import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import FormInputWrapper from '../form-input-wrapper/FormInputWrapper.jsx';

import {
  addValidationField,
  removeValidationField,
  addDependentsToIgnoreList,
  removeDependentsFromIgnoreList,
  addPreventFormSubmissionField,
  removePreventFormSubmissionField
} from '../redux/form/form.actions';

import {
  uuidv4,
  filterArrayByProperty,
  removeItemsFromArrayByProperty
} from '../_global/js/form.utils';

const FormCheckbox = ({
  groupId,
  inputLabel,
  inputDescriptionText,
  parentElementClass,
  inputName,
  inputRequired,
  inputOptions,
  inputDefaults,
  sectionLabel,
  inputValidationLabel,
  inputValidationMessage,
  conditionalInputs,
  dependentInputs,
  additionalInputs,
  preventProgression,
  inputTestData,
  autoPopulate,
  triggerValidation,
  formStepValidation,
  addValidationField,
  removeValidationField,
  addDependentsToIgnoreList,
  removeDependentsFromIgnoreList,
  addPreventFormSubmissionField,
  removePreventFormSubmissionField
  }) => {
  const inputId = useRef([]);
  const [ inputValue, setInputValue ] = useState([]);
  const [ showAdditional, setShowAdditional ] = useState(false);
  const [ isValid, setIsValid ] = useState(true);
  const [ requiresFormValidation, setRequiresFormValidation ] = useState(false);
  const [ conditionalQuestions, setConditionalQuestions ] = useState(null);
  const validationLabel = inputValidationLabel || inputLabel;
  const errorMessage = inputValidationMessage || 'This field is required.';

  // Create a unique ID for each input
  if (inputId.current.length === 0) {
    for (let i = 0, j = inputOptions.length; i < j; ++i) {
      inputId.current.push( uuidv4() );
    }
  }

  /**
   * When the component has been mounted
   */
  useEffect(() => {
    let nonMatchingDependentFieldReferencesArray = [];

    // Mark this field as required, as necessary
    if (inputRequired) {
      setRequiresFormValidation(true);
    }

    // Automatically hide any dependent inputs
    if (dependentInputs !== undefined && dependentInputs !== null) {
      // Capture all the input names
      for (let i = 0, j = dependentInputs.length; i < j; ++i) {
        nonMatchingDependentFieldReferencesArray = nonMatchingDependentFieldReferencesArray.concat(dependentInputs[i].inputs);
      }

      addDependentsToIgnoreList(nonMatchingDependentFieldReferencesArray);
    }

    // Set any default values and automatically validate the input
    if (inputDefaults) {
        setInputValue(inputDefaults);

        // Remove this field from the invalid list
        setRequiresFormValidation(false);
    }

    // Cleanup the component (unmount)
    return () => {
      // Remove this field from the invalid list
      removeValidationField(inputId.current[0]);

      // Remove this field from the list preventing the form submission (if applicable)
      removePreventFormSubmissionField(inputId.current[0]);
    };
  }, []);

  /**
   * When the field validation requirement has changed
   */
  useEffect(() => {
    if (requiresFormValidation) {
      // Add this field to the form validation state
      addValidationField({
        id: inputId.current[0],
        label: validationLabel,
        sectionLabel: sectionLabel,
        required: inputRequired,
        formStep: formStepValidation
      });
    } else {
      // Remove this field from the form validation state
      removeValidationField(inputId.current[0]);
    }
  }, [requiresFormValidation]);

  /**
   * When the component has been instructed to validate it's input
   */
  useEffect(() => {
    if (triggerValidation !== null) {

      // Check if this input is in the current form step
      if (triggerValidation.formStep === formStepValidation) {
        // Field is required, but failed validation
        if (inputRequired && inputValue.length === 0) {
          // Display the error message
          setIsValid(false);

          // Mark this field as invalid
          setRequiresFormValidation(true);
        }
      }
    }
  }, [triggerValidation]);

  /**
   * When the component has been instructed to auto-populate content
   */
  useEffect(() => {
    // Populate the field with test data
    if (autoPopulate && inputTestData) {
      setInputValue([inputTestData]);
      setIsValid(true);
      setRequiresFormValidation(false);
    }
  }, [autoPopulate]);

  /**
   * When the selected values of the checkbox(s) changes
   */
  useEffect(() => {
    let conditionalFieldsArray = null;
    let conditionalFieldsData = null;
    let matchingDependentFieldsArray = [];
    let matchingDependentFieldNamesArray = [];
    let matchingDependentInputsResponseArray = [];
    let nonMatchingDependentFieldsArray = [];
    let nonMatchingDependentFieldReferencesArray = [];
    let preventFormSubmission = false;

    /**
     * Check if the user has entered a value and if there are
     * further actions to take. e.g. additional fields or checks.
     */
    if (inputValue.length !== 0) {

      if (conditionalInputs) {
        // If conditional inputs have been provided, check for any matches
        conditionalFieldsArray = [];
        conditionalFieldsData = [];

        inputValue.map( arrayValue => {
          conditionalFieldsArray = [...conditionalFieldsArray, ...filterArrayByProperty(conditionalInputs, 'responseMatch', arrayValue)]
        });

        if (conditionalFieldsArray.length !== 0) {
          // Extract all the input Array data from the matching Objects
          conditionalFieldsArray.map( inputsObject => {
            conditionalFieldsData = [...conditionalFieldsData, ...inputsObject.inputs]
          });

          // Assign an ID to each input
          for (let i = 0, j = conditionalFieldsData.length; i < j; ++i) {
            conditionalFieldsData[i].id = uuidv4();
          }

          setConditionalQuestions(conditionalFieldsData);
        } else {
          setConditionalQuestions(null);
        }
      }

      if (dependentInputs !== undefined && dependentInputs !== null) {
        // If dependent inputs have been provided, update the ignore list as necessary

        // Loop over the input responses
        for (let k = 0, b = inputValue.length; k < b; ++k) {
          // Capture any dependent inputs that match this response
          matchingDependentFieldsArray = matchingDependentFieldsArray.concat(
            filterArrayByProperty(dependentInputs, 'responseMatch', inputValue[k])
          );
        }

        /**
         * Note that we hide inputs BEFORE showing any.
         * This order is important so that the same inputs can appear in
         * different lists of dependent inputs.
         * That is, if the same input appears in multiple responseMatch
         * checks, we want to make sure it's still visible after the
         * update to the state.
         */
        if (matchingDependentFieldsArray.length !== 0) {

          // One or more dependents can be shown
          for (let x = 0, r = matchingDependentFieldsArray.length; x < r; ++x) {
            // Capture all the input names
            matchingDependentFieldNamesArray = matchingDependentFieldNamesArray.concat(matchingDependentFieldsArray[x].inputs);

            // Generate the list of matching input names
            matchingDependentInputsResponseArray = matchingDependentInputsResponseArray.concat(matchingDependentFieldsArray[x].responseMatch);
          }

          // Generate the list of non-matching dependents
          nonMatchingDependentFieldsArray = nonMatchingDependentFieldsArray.concat(
            removeItemsFromArrayByProperty(dependentInputs, 'responseMatch', matchingDependentInputsResponseArray)
          );
        } else {
          // No matching dependents found, so the non-matching dependents are ALL of the dependents
          nonMatchingDependentFieldsArray = nonMatchingDependentFieldsArray.concat(dependentInputs);
        }

        /**
         * Note that we hide fields BEFORE showing any.
         * This order is important so that the same fields can appear in
         * different lists of dependent fields.
         * That is, if the same input appears in multiple responseMatch
         * checks, we want to make sure it's still visible after the
         * update to the state.
         */
        if (nonMatchingDependentFieldsArray.length !== 0) {
          // One or more dependents should be hidden
          // Capture all the input names
          for (let i = 0, j = nonMatchingDependentFieldsArray.length; i < j; ++i) {
            nonMatchingDependentFieldReferencesArray = nonMatchingDependentFieldReferencesArray.concat(nonMatchingDependentFieldsArray[i].inputs);
          }

          addDependentsToIgnoreList(nonMatchingDependentFieldReferencesArray);
        }

        if (matchingDependentFieldNamesArray.length !== 0) {
          removeDependentsFromIgnoreList(matchingDependentFieldNamesArray);
        }
      }

      // Check if the response should prevent the form progress and submission
      if (preventProgression !== undefined && preventProgression !== null) {
        // Loop over the input responses
        for (let k = 0, b = inputValue.length; k < b; ++k) {
          // Check for matching values
          for (let i = 0, j = preventProgression.length; i < j; ++i) {
            if (inputValue[k] === preventProgression[i]) {
              // A match was found
              preventFormSubmission = true;
              break;
            }
          }

          // We only need to find the first match
          if (preventFormSubmission) {
            break;
          }
        }

        if (preventFormSubmission) {
          // Prevent the form submission
          addPreventFormSubmissionField(inputId.current[0]);
        } else {
          // Re-enable form submission
          removePreventFormSubmissionField(inputId.current[0]);
        }
      }
    } else {
      // Reset any conditional questions
      if (conditionalQuestions !== null) {
        setConditionalQuestions(null);
      }

      // Reset any dependent questions
      resetDependentQuestions(dependentInputs);

      // Re-enable form submission
      removePreventFormSubmissionField(inputId.current[0]);
    }

    // Additional check to prevent submission if unchecked
    if (
      inputValue.length === 0 &&
      preventProgression &&
      preventProgression[0] === ""
    ) {
      addPreventFormSubmissionField(inputId.current[0]);
    }
  }, [inputValue]);

  /**
   * Response to clicks on the checkbox inputs.
   * Update the local state to add or remove a checked value
   *
   * @param {Change Event} evt Event that triggered the input to update
   *
   * @returns void
   */
  const onInputChange = (evt) => {
    const checkboxValue = evt.target.getAttribute('value');
    const isAlreadyChecked = inputIsChecked(checkboxValue);
    let updatedInputValue = [];

    if (!isAlreadyChecked) {
      // This option has just been "checked", capture the value
      updatedInputValue = [...inputValue, checkboxValue];
    } else {
      // This option was already "checked", remove the value
      updatedInputValue = inputValue.filter( selectedValue => {
        return selectedValue !== checkboxValue;
      });
    }

    // Capture the new value(s)
    setInputValue(updatedInputValue);

    // Field is required, but failed validation
    if (inputRequired && updatedInputValue.length === 0) {
      // Display the error message
      setIsValid(false);

      // Mark this field as invalid
      setRequiresFormValidation(true);
    }

    // Field passed validation
    if (updatedInputValue.length !== 0) {
      // Update the validation message
      setIsValid(true);

      // Remove this field from the invalid list
      setRequiresFormValidation(false);
    }
  };

  /**
   * Check if a value is already in our local state
   *
   * @param {String} checkboxValue The "value" attribute of the checkbox input
   *
   * @returns Boolean
   */
  const inputIsChecked = (checkboxValue) => {
    const filteredValues = inputValue.filter( selectedValue => {
      return selectedValue === checkboxValue;
    });
    let isChecked = true;

    if (filteredValues.length === 0) {
      isChecked = false;
    }

    return isChecked;
  };

  const onAdditionalButtonClick = (evt) => {
    evt.preventDefault();

    if (!showAdditional) {
      setShowAdditional(true);
    } else {
      setShowAdditional(false);
    }
  };

  /**
   * Capture the input names of all questions that are
   * dependnent on the response of this question and
   * add them global ignore list
   *
   * @param {Array} dependentInputsConfiguration The entire dependent questions configuration
   *
   * @returns void
   */
  function resetDependentQuestions(dependentInputsConfiguration) {
    let nonMatchingDependentFieldReferencesArray = [];

    if (dependentInputsConfiguration !== undefined && dependentInputsConfiguration !== null) {
      // Loop over the possible dependent questions
      for (let k = 0, b = dependentInputsConfiguration.length; k < b; ++k) {
        // Capture all dependent inputs
        nonMatchingDependentFieldReferencesArray = nonMatchingDependentFieldReferencesArray.concat(dependentInputsConfiguration[k].inputs);
      }

      addDependentsToIgnoreList(nonMatchingDependentFieldReferencesArray);
    }
  }

  return (
    <div className={parentElementClass}>
      <div>
        <fieldset>
          <legend className="qld__label">{ (inputRequired) ? <abbr title="required">*</abbr> : null } {inputLabel}</legend>
          {
            (inputDescriptionText !== undefined && inputDescriptionText !== null) ? <span id={`${inputId.current}-description`} className="qld__hint-text">{inputDescriptionText}</span> : null
          }
          {
            (!isValid) ?
              <p className="sq-form-error">
                {errorMessage}
              </p>
            : null
          }
          <ul className="custom-online-form__response-list">
            {
              inputOptions.map( (checkbox, index) => {
                return (
                  <li key={inputId.current[index]}>
                    <input
                      id={inputId.current[index]}
                      className={!isValid ? "qld__input--error" : null}
                      name={inputName}
                      value={checkbox.value}
                      type="checkbox"
                      onChange={onInputChange}
                      checked={ inputIsChecked(checkbox.value) }
                      autoComplete="off"
                      data-groupid={groupId}
                      data-validation-label={validationLabel}
                      data-section-label={sectionLabel}
                    />
                    <label htmlFor={inputId.current[index]}>{checkbox.label}</label>
                  </li>
                  );
              })
            }
          </ul>
        </fieldset>
      </div>
      {
        (conditionalQuestions) ?
          <div>
            {
              conditionalQuestions.map( (formInput) => {
                const formInputConfig = { ...formInput };

                /**
                 * If provided, add the section legend (label) to the input configuration.
                 * This is so we can include the section label in the form validation messages and summary.
                 */
                if (sectionLabel) {
                  formInputConfig.sectionLabel = sectionLabel;
                }

                return (
                  <FormInputWrapper
                    key={formInputConfig.id}
                    inputConfiguration={formInputConfig}
                    autoPopulate={autoPopulate}
                    triggerValidation={triggerValidation}
                    formStepValidation={formStepValidation}
                  />
                );
              })
            }
          </div>
        : null
      }
      {
        (additionalInputs && !showAdditional) ? <button onClick={onAdditionalButtonClick} className="qld__btn">{additionalInputs.buttonLabel}</button> : null
      }
      {
        (showAdditional) ?
          <div>
            <div>
              <button onClick={onAdditionalButtonClick} className="qld__btn qld__btn--secondary">Remove this section</button>
            </div>
            {
              additionalInputs.inputs.map( (formInput, index) => {
                const formInputConfig = { ...formInput };

                /**
                 * If provided, add the section legend (label) to the input configuration.
                 * This is so we can include the section label in the form validation messages and summary.
                 */
                if (sectionLabel) {
                  formInputConfig.sectionLabel = sectionLabel;
                }

                return (
                  <FormInputWrapper
                    key={index}
                    inputConfiguration={formInputConfig}
                    autoPopulate={autoPopulate}
                    triggerValidation={triggerValidation}
                    formStepValidation={formStepValidation}
                  />
                );
              })
            }
          </div>
        : null
      }
    </div>
  );
};

FormCheckbox.propTypes = {
  "groupId": PropTypes.string,
  "inputLabel": PropTypes.string.isRequired,
  "inputDescriptionText": PropTypes.string,
  "parentElementClass": PropTypes.string,
  "inputName": PropTypes.string.isRequired,
  "inputRequired": PropTypes.bool.isRequired,
  "inputOptions": PropTypes.array.isRequired,
  "inputDefaults": PropTypes.array,
  "sectionLabel": PropTypes.string,
  "inputValidationLabel": PropTypes.string,
  "inputValidationMessage": PropTypes.string,
  "conditionalInputs": PropTypes.array,
  "dependentInputs": PropTypes.array,
  "additionalInputs": PropTypes.object,
  "preventProgression": PropTypes.array,
  "inputTestData": PropTypes.string,
  "autoPopulate": PropTypes.bool,
  "triggerValidation": PropTypes.object,
  "formStepValidation": PropTypes.number.isRequired,
  "addValidationField":  PropTypes.oneOfType( [PropTypes.object, PropTypes.func] ),
  "removeValidationField":  PropTypes.oneOfType( [PropTypes.string, PropTypes.func] ),
  "addDependentsToIgnoreList":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "removeDependentsFromIgnoreList":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "addPreventFormSubmissionField":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "removePreventFormSubmissionField":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] )
};

const mapDispatchToProps = (dispatch) => {
  return {
    "addValidationField": formFieldData => dispatch(addValidationField(formFieldData)),
    "removeValidationField": formFieldData => dispatch(removeValidationField(formFieldData)),
    "addDependentsToIgnoreList": inputReferences => dispatch(addDependentsToIgnoreList(inputReferences)),
    "removeDependentsFromIgnoreList": inputReferences => dispatch(removeDependentsFromIgnoreList(inputReferences)),
    "addPreventFormSubmissionField": inputNames => dispatch(addPreventFormSubmissionField(inputNames)),
    "removePreventFormSubmissionField": inputNames => dispatch(removePreventFormSubmissionField(inputNames))
  };
};

export default connect(null, mapDispatchToProps)(FormCheckbox);